import React from 'react';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Bell, BellOff, Wand2, Send } from 'lucide-react';

/**
 * Shows the count of reminders sent for an AMC / Hosting row. Click opens
 * a popover listing each log entry — newest first.
 *
 * Each entry in `reminders_sent` may have:
 *   - trigger:   int (days before expiry) for cron sends, "manual" for manual
 *   - source:    "cron" | "manual"          (added Jun 13, 2026; older rows lack this)
 *   - sent_at:   ISO timestamp with timezone (added Jun 13, 2026)
 *   - sent_on:   ISO yyyy-mm-dd (legacy fallback)
 *   - recipients: list[str]
 *   - triggered_by_name: str (manual sends only)
 *
 * Resets to 0 on the row's next Start Date update — see
 * `_hosting_prepare_update` / `_amc_prepare_update` in server.py.
 */
const formatStamp = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', hour12: true });
};

const triggerLabel = (entry) => {
  const t = entry?.trigger;
  if (typeof t === 'number' || (typeof t === 'string' && /^\d+$/.test(t))) {
    const n = Number(t);
    return `${n}-day reminder`;
  }
  if (t === 'manual' || entry?.source === 'manual') return 'Manual send';
  return 'Reminder';
};

const isManual = (entry) => entry?.source === 'manual' || entry?.trigger === 'manual';

const RemindersCell = ({ row, testIdPrefix = 'reminders' }) => {
  const log = Array.isArray(row?.reminders_sent) ? row.reminders_sent : [];
  const count = log.length;

  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-${row.id}-empty`}>
        <BellOff className="h-3.5 w-3.5 opacity-60" />
        <span>—</span>
      </span>
    );
  }

  // newest first — prefer sent_at, fall back to sent_on
  const sorted = [...log].sort((a, b) => {
    const ax = a.sent_at || a.sent_on || '';
    const bx = b.sent_at || b.sent_on || '';
    return bx.localeCompare(ax);
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-medium hover:bg-muted hover:border-primary/40 transition-colors"
          data-testid={`${testIdPrefix}-${row.id}-trigger`}
        >
          <Bell className="h-3.5 w-3.5 text-primary" />
          <span>{count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 max-h-96 overflow-y-auto p-0"
        data-testid={`${testIdPrefix}-${row.id}-popover`}
      >
        <div className="sticky top-0 bg-background border-b border-border px-3 py-2">
          <p className="text-sm font-semibold">Reminders Log</p>
          <p className="text-[11px] text-muted-foreground">
            {count} reminder{count === 1 ? '' : 's'} sent for this {row?.server_name ? 'hosting' : 'AMC'} account
          </p>
        </div>
        <ul className="divide-y divide-border">
          {sorted.map((entry, idx) => {
            const manual = isManual(entry);
            const when = formatStamp(entry.sent_at || entry.sent_on);
            const recipients = Array.isArray(entry.recipients) ? entry.recipients : [];
            const recipientsLabel = recipients.length === 0
              ? '—'
              : recipients.length <= 2
                ? recipients.join(', ')
                : `${recipients.slice(0, 2).join(', ')} +${recipients.length - 2} more`;
            return (
              <li key={idx} className="px-3 py-2.5 text-xs space-y-1" data-testid={`${testIdPrefix}-entry-${idx}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{when}</span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] gap-1 ${manual ? 'border-amber-500/40 text-amber-500' : 'border-emerald-500/40 text-emerald-500'}`}
                  >
                    {manual ? <Send className="h-3 w-3" /> : <Wand2 className="h-3 w-3" />}
                    {manual ? 'Manual' : 'Auto'}
                  </Badge>
                </div>
                <div className="text-muted-foreground">
                  {triggerLabel(entry)}
                  {manual && entry.triggered_by_name && (
                    <span> · by <span className="text-foreground">{entry.triggered_by_name}</span></span>
                  )}
                </div>
                <div className="text-muted-foreground truncate" title={recipients.join(', ')}>
                  To: <span className="text-foreground">{recipientsLabel}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default RemindersCell;
